import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { CameraOutlined, DeleteOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  blobToBase64,
  feedPostRequest,
  FeedPostRequestError,
  type FeedPostEligibility,
} from '../../lib/feedPostApi';
import { compressMatchPhoto } from '../../shared/utils/match-photo';
import { FEED_POST_DAILY_LIMIT, FEED_POST_MAX_LENGTH, parseFeedLink } from '../../shared/utils/feed-post';
import { showNotification } from '../../shared/utils/notifications';
import FeedPartnerForm from './FeedPartnerForm';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

const errorKey = (error: unknown) => {
  const { status, code } = error as FeedPostRequestError;
  if (code === 'BLOCKED') return 'blocked';
  if (code === 'LINK_SHORTENER') return 'linkShortener';
  if (code === 'LINK_INVALID') return 'linkInvalid';
  if (code === 'TEXT') return 'textTooLong';
  if (code === 'EMPTY') return 'empty';
  if (code?.startsWith('NOT_ALLOWED_')) return `reason.${code.slice('NOT_ALLOWED_'.length)}`;
  if (status === 413 || status === 415) return 'photoError';
  return 'saveError';
};

/** Ana sayfa ve kendi lig sayfasının Akış sekmesinde paylaşım kutusu: yazı, en çok 1 fotoğraf ve 1 bağlantı. Şartı sunucu denetler. */
export default function FeedComposer({ onPosted }: { onPosted: () => void }) {
  const { t } = useTranslation('leagues');
  const [eligibility, setEligibility] = useState<FeedPostEligibility | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [open, setOpen] = useState(false);
  // B2: "Partner arıyorum" formu (maç şartı yok; ligde olmak yeter).
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setStatus('loading');
    try {
      setEligibility(await feedPostRequest<FeedPostEligibility>('GET'));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!photo) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const linkError = link.trim() ? parseFeedLink(link) : null;
  const linkProblem = typeof linkError === 'string' ? linkError : null;
  const empty = !text.trim() && !photo && !link.trim();

  const reset = () => {
    setOpen(false);
    setText('');
    setLink('');
    setPhoto(null);
    setError('');
  };

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!selected) return;
    if (!ACCEPTED_TYPES.includes(selected.type) || selected.size > MAX_SOURCE_BYTES) {
      setError(t('detail.posts.photoFormat'));
      return;
    }
    setError('');
    setPhoto(selected);
  };

  const submit = async () => {
    if (busy || empty || linkProblem) return;
    setBusy(true);
    setError('');
    try {
      const encoded = photo ? await blobToBase64(await compressMatchPhoto(photo)) : undefined;
      await feedPostRequest('POST', {}, { text, link: link.trim(), photo: encoded });
      reset();
      showNotification('success', t('detail.posts.posted'));
      setEligibility(current => current && {
        ...current,
        remaining: Math.max(0, current.remaining - 1),
        canPost: current.remaining > 1,
        reason: current.remaining > 1 ? null : 'limit',
      });
      onPosted();
    } catch (failure) {
      setError(t(`detail.posts.${errorKey(failure)}`));
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') {
    return <p className="feed-composer feed-composer--muted" role="status">{t('detail.posts.checking')}</p>;
  }
  if (status === 'error') {
    return (
      <div className="feed-composer feed-composer--muted" role="alert">
        <span>{t('detail.posts.checkError')}</span>
        <Button size="small" onClick={load}>{t('detail.posts.retry')}</Button>
      </div>
    );
  }
  const partnerAllowed = !!eligibility && (eligibility.canPost || eligibility.reason === 'match');
  if (partnerOpen) {
    return (
      <FeedPartnerForm
        onCancel={() => setPartnerOpen(false)}
        onPosted={() => {
          setPartnerOpen(false);
          showNotification('success', t('detail.posts.posted'));
          onPosted();
        }}
      />
    );
  }
  const partnerButton = partnerAllowed ? (
    <Button className="feed-composer__partner" onClick={() => setPartnerOpen(true)}>
      <span aria-hidden="true">🎾</span> {t('detail.posts.partner.start')}
    </Button>
  ) : null;
  if (!eligibility?.canPost && !open) {
    return (
      <div className="feed-composer-row">
        <p className="feed-composer feed-composer--muted">
          <EditOutlined aria-hidden="true" /> {t(`detail.posts.reason.${eligibility?.reason || 'match'}`)}
        </p>
        {partnerButton}
      </div>
    );
  }
  if (!open) {
    return (
      <div className="feed-composer-row">
        <button type="button" className="feed-composer feed-composer__start" onClick={() => setOpen(true)}>
          <EditOutlined aria-hidden="true" />
          <span>{t('detail.posts.start')}</span>
        </button>
        {partnerButton}
      </div>
    );
  }

  return (
    <form
      className="feed-composer feed-composer--open"
      aria-busy={busy}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor="feed-composer-text" className="feed-composer__label">{t('detail.posts.textLabel')}</label>
      <Input.TextArea
        id="feed-composer-text"
        value={text}
        onChange={event => setText(event.target.value)}
        maxLength={FEED_POST_MAX_LENGTH}
        showCount
        autoSize={{ minRows: 3, maxRows: 8 }}
        placeholder={t('detail.posts.placeholder')}
        disabled={busy}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- kullanıcı paylaşım kutusunu açtı
        autoFocus
      />
      {preview && (
        <div className="feed-composer__preview">
          <img src={preview} alt={t('detail.posts.photoPreview')} />
          <Button
            type="text"
            icon={<DeleteOutlined aria-hidden="true" />}
            onClick={() => setPhoto(null)}
            disabled={busy}
            aria-label={t('detail.posts.photoRemove')}
            title={t('detail.posts.photoRemove')}
          />
        </div>
      )}
      <label htmlFor="feed-composer-link" className="feed-composer__label">{t('detail.posts.linkLabel')}</label>
      <Input
        id="feed-composer-link"
        type="url"
        inputMode="url"
        value={link}
        onChange={event => setLink(event.target.value)}
        prefix={<LinkOutlined aria-hidden="true" />}
        placeholder={t('detail.posts.linkPlaceholder')}
        disabled={busy}
        status={linkProblem ? 'error' : undefined}
        aria-invalid={!!linkProblem}
        aria-describedby="feed-composer-link-hint"
      />
      <p id="feed-composer-link-hint" className={`feed-composer__hint${linkProblem ? ' is-error' : ''}`}>
        {linkProblem
          ? t(linkProblem === 'LINK_SHORTENER' ? 'detail.posts.linkShortener' : 'detail.posts.linkInvalid')
          : t('detail.posts.linkHint')}
      </p>
      <input
        ref={inputRef}
        className="play-sr-only"
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={choose}
        disabled={busy}
        tabIndex={-1}
        aria-hidden="true"
      />
      <p className="feed-composer__hint">
        {t('detail.posts.disclosure', { count: eligibility?.remaining ?? FEED_POST_DAILY_LIMIT })}
      </p>
      {error && <Alert role="alert" showIcon type="error" message={error} />}
      <div className="feed-composer__actions">
        {!photo && (
          <Button icon={<CameraOutlined aria-hidden="true" />} onClick={() => inputRef.current?.click()} disabled={busy}>
            {t('detail.posts.photoAdd')}
          </Button>
        )}
        <span className="feed-composer__spacer" />
        <Button type="text" onClick={reset} disabled={busy}>{t('detail.posts.cancel')}</Button>
        <Button
          htmlType="submit"
          className="feed-composer__submit"
          loading={busy}
          disabled={empty || !!linkProblem}
          title={empty ? t('detail.posts.empty') : linkProblem ? t('detail.posts.linkInvalid') : undefined}
        >
          {t('detail.posts.submit')}
        </Button>
      </div>
    </form>
  );
}
